import {
  ATTESTATION_HELENA,
  ATTESTATION_HELENA_ERROR,
  ATTESTATION_HELENA_START,
  CHECK_HELENA_ENABLED_ERROR,
  CHECK_HELENA_ENABLED_START,
  CHECK_HELENA_ENABLED_SUCCESS,
  ENABLE_HELENA_ERROR,
  ENABLE_HELENA_START,
  ENABLE_HELENA_SUCCESS,
  HelenaAction,
  PATIENT_ID_HELENA_ERROR,
  PATIENT_ID_HELENA_START,
  PATIENT_ID_HELENA_SUCCESS,
  PATIENT_STATUS_HELENA_START,
  PATIENT_STATUS_HELENA_SUCCESS,
  PRESCRIPTIONS_HELENA,
  PRESCRIPTIONS_HELENA_START,
  REGISTRATION_FINISHED_HELENA_ERROR,
  REGISTRATION_FINISHED_HELENA_START,
  REGISTRATION_FINISHED_HELENA_SUCCESS,
  REGISTRATION_LINK_HELENA_ERROR,
  REGISTRATION_LINK_HELENA_START,
  REGISTRATION_LINK_HELENA_SUCCESS,
  SESSION_HELENA_ERROR,
  SESSION_HELENA_START,
  SESSION_HELENA_SUCCESS,
} from "store/types/helena.types";
import { NotificationAction } from "store/types/notification.types";
import * as HelenaApi from "api/helena.api";
import * as UserConfigurationApi from "api/user-configuration";
import { AnyAction, Dispatch } from "redux";
import {
  HelenaDocument,
  HelenaDocumentType,
  HelenaDocumentTypeEnum,
  HelenaPatientStatusEnum,
  HelenaPrescription,
  HelenaPrescriptionType,
  HelenaSessionKey,
} from "model/helena.model";
import { UserConfiguration } from "model/user-configuration.model";
import { Patient } from "model/patient.model";
import { HelenaRegistration } from "model/helena-registration.model";
import {
  dispatchError,
  dispatchHelenaNotRegistered,
  dispatchResetNotification,
} from "./notification.actions";
import { PrescriptionType } from "model/prescription.model";
import { ThunkAction } from "redux-thunk";
import { GlobalState } from "store/configure-store";
import { Error, ErrorTypeEnum } from "model/error.model";
import { i18nPatientContext, i18nUserContext } from "config/translations/I18n";
import { dispatchSetUserConfiguration } from "./user-context.actions";

/** ---- ACTIONS ---- */
export const createAttestationHE = (
  patientId: number,
  userId: number,
  helenaSessionKey: HelenaSessionKey | null,
  helenaPatientId: string | null,
  pdf: string
): ThunkAction<Promise<void>, GlobalState, any, HelenaAction> => {
  return async (dispatch) => {
    if (helenaSessionKey === null || helenaPatientId === null) {
      dispatch(
        dispatchHelenaAttestationError("No helenaSessionKey or PatientId")
      );
      return Promise.reject();
    }
    const helenaDocument = new HelenaDocument({
      documentType: HelenaDocumentTypeEnum.ATTESTATION,
      title: i18nPatientContext.t("helena.attestation.title"),
      conclusion: "",
      content: pdf,
    });
    dispatch(dispatchHelenaAttestationStart(helenaDocument));
    try {
      await HelenaApi.uploadAttestation(
        patientId,
        userId,
        helenaSessionKey,
        helenaPatientId,
        helenaDocument
      );
      return dispatch(dispatchHelenaAttestation(helenaDocument));
    } catch (e) {
      dispatch(dispatchHelenaAttestationError(e));
      throw new Error({ message: "", type: ErrorTypeEnum.ATTESTATION_HELENA });
    }
  };
};

export const createPrescriptionHE = (
  patientId: number,
  userId: number,
  helenaSessionKey: HelenaSessionKey | null,
  helenaPatientId: string | null,
  prescriptions: Array<PrescriptionType>
): ThunkAction<Promise<void>, GlobalState, any, HelenaAction> => {
  return async (dispatch) => {
    try {
      if (helenaSessionKey === null || helenaPatientId === null) {
        return Promise.reject();
      }
      const hPrescriptions: Array<HelenaPrescriptionType> = [];
      prescriptions.forEach((p) => {
        hPrescriptions.push(
          new HelenaPrescription({
            prescriptionId: p.id,
            pdf: p.pdf || "",
          })
        );
      });

      dispatch(dispatchHelenaPrescriptionsStart(hPrescriptions));
      await HelenaApi.uploadPrescriptions(
        patientId,
        userId,
        hPrescriptions,
        helenaSessionKey,
        helenaPatientId
      );

      dispatch(dispatchHelenaPrescriptionsSuccess());
    } catch (e) {
      dispatch(dispatchResetNotification());
      return Promise.reject(
        new Error({
          type: ErrorTypeEnum.PRESCRIPTION_HELENA,
          message: i18nUserContext.t("medicationFlow.error.prescriptionRE"),
        })
      );
    }
  };
};

export const checkHelenaEnabled = (userId: number) => {
  return async (dispatch: Dispatch<HelenaAction>) => {
    try {
      dispatchCheckHelenaEnabledStart()(dispatch);
      const config = await UserConfigurationApi.getUserConfiguration(userId);
      return dispatchCheckHelenaEnabledSuccess(config.helenaEnabled)(dispatch);
    } catch (e) {
      dispatchCheckHelenaEnabledError()(dispatch);
    }
  };
};

export const isHelenaEnabledInConfig = (
  userConfiguration: UserConfiguration
) => {
  return (dispatch: Dispatch<HelenaAction>) => {
    const enabled = userConfiguration.helenaEnabled;
    dispatchCheckHelenaEnabledSuccess(enabled)(dispatch);
    return enabled;
  };
};

export const enableHelena = (
  userId: number
): ThunkAction<Promise<HelenaSessionKey>, GlobalState, any, HelenaAction> => {
  return async (dispatch, getState) => {
    try {
      dispatch(dispatchEnableHelenaStart());
      const config = await UserConfigurationApi.getUserConfiguration(userId);
      const withHelena = {
        ...config,
        helenaEnabled: true,
        helenaActivationQuestionVisit: true,
      };
      const updatedUserConfiguration =
        await UserConfigurationApi.updateUserConfiguration(
          userId,
          withHelena
        ).then((userConfiguration) => {
          dispatch(dispatchSetUserConfiguration(userConfiguration));
          return userConfiguration;
        });

      if (updatedUserConfiguration.helenaEnabled) {
        dispatch(dispatchEnableHelenaSuccess());
        await dispatch(await createHelenaSession(userId));
      } else {
        dispatch(dispatchEnableHelenaError());
      }
    } catch (e) {
      dispatch(dispatchEnableHelenaError());
      return Promise.resolve(e);
    }
  };
};

export const disableHelenaActivationQuestion = (
  userId: number
): ThunkAction<Promise<void>, GlobalState, any, HelenaAction> => {
  return async (dispatch, getState) => {
    const config = await UserConfigurationApi.getUserConfiguration(userId);
    const withHelenaActivationQuestion = {
      ...config,
      helenaActivationQuestionVisit: true,
    };
    const userConfiguration =
      await UserConfigurationApi.updateUserConfiguration(
        userId,
        withHelenaActivationQuestion
      );

    dispatch(dispatchSetUserConfiguration(userConfiguration));

    return Promise.resolve();
  };
};

export const createHelenaSession = (
  userId: number
): ThunkAction<Promise<HelenaSessionKey>, GlobalState, any, AnyAction> => {
  return async (dispatch) => {
    try {
      dispatch(dispatchHelenaSessionStart());
      const sessionKey = await HelenaApi.createSession(userId);
      dispatch(dispatchHelenaSessionSuccess(sessionKey));
      return Promise.resolve(sessionKey);
    } catch (e) {
      dispatch(dispatchHelenaSessionError());
      return Promise.reject(e);
    }
  };
};

export const loadHelenaPatient = (
  userId: number,
  sessionKey: HelenaSessionKey | null,
  patient: Patient
): ThunkAction<
  Promise<void>,
  GlobalState,
  any,
  HelenaAction | NotificationAction
> => {
  return async (dispatch) => {
    if (patient.inss) {
      if (sessionKey) {
        let patientId;
        let helenPatientStatus;
        try {
          if (isSessionHelenaExpired(sessionKey)) {
            sessionKey = await dispatch(createHelenaSession(userId));
          }

          //Patient ID Loading
          dispatchHelenaPatientIdStart()(dispatch);
          patientId = await HelenaApi.loadPatientId(
            userId,
            sessionKey,
            patient.inss
          );
          dispatchHelenaPatientIdSuccess(patientId)(dispatch);

          dispatchHelenaPatientStatusStart()(dispatch);
          helenPatientStatus = await HelenaApi.loadPatientStatus(
            userId,
            sessionKey,
            patientId
          );
          dispatchHelenaPatientStatusSuccess(helenPatientStatus)(dispatch);

          helenPatientStatus === HelenaPatientStatusEnum.NOT_REGISTERED &&
            (await dispatch(
              loadHelenaRegistrationLink(userId, sessionKey, patient)
            ));
          return Promise.resolve();
        } catch (e) {
          !patientId && dispatchHelenaPatientIdError(e)(dispatch);
          patientId &&
            !helenPatientStatus &&
            dispatchHelenaPatientStatusError(e)(dispatch);
          return Promise.reject(e);
        }
      }
    } else {
      dispatchError(i18nUserContext.t("helena.attestation.insz"))(dispatch);
      return Promise.reject();
    }
  };
};

export const loadHelenaRegistrationLink = (
  userId: number,
  sessionKey: HelenaSessionKey,
  patient: Patient
): ThunkAction<Promise<void>, GlobalState, any, HelenaAction> => {
  return async (dispatch) => {
    try {
      dispatch(dispatchHelenaRegistrationLinkStart());
      const registrationLink = await HelenaApi.retrieveRegistrationLink(
        userId,
        sessionKey,
        patient
      );
      dispatch(dispatchHelenaRegistrationLinkSuccess(registrationLink));
    } catch (e) {
      dispatch(dispatchHelenaRegistrationLinkError());
    }
  };
};

export const helenaRegistrationFinished = (
  userId: number,
  sessionKey: HelenaSessionKey,
  inss: string
): ThunkAction<
  Promise<string | undefined>,
  GlobalState,
  any,
  HelenaAction | NotificationAction
> => {
  return async (dispatch) => {
    try {
      dispatch(dispatchHelenaRegistrationFinishedStart());
      const response = await HelenaApi.retrieveRegistrationFinished(
        userId,
        sessionKey,
        inss
      );
      dispatch(dispatchHelenaRegistrationFinishedSuccess(response));

      dispatch(dispatchHelenaPatientStatusStart());
      const helenPatientStatus = await HelenaApi.loadPatientStatus(
        userId,
        sessionKey,
        response.patientId
      );
      dispatch(dispatchHelenaPatientStatusSuccess(helenPatientStatus));

      return response.pdf;
    } catch (e) {
      dispatch(dispatchHelenaRegistrationFinishedError());
      dispatch(dispatchHelenaNotRegistered());
    }
  };
};

/** ---- DISPATCHERS ---- */
const dispatchCheckHelenaEnabledStart = () => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: CHECK_HELENA_ENABLED_START,
    });
  };
};

const dispatchCheckHelenaEnabledSuccess = (enabled: boolean) => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: CHECK_HELENA_ENABLED_SUCCESS,
      payload: enabled,
    });
  };
};

const dispatchCheckHelenaEnabledError = () => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: CHECK_HELENA_ENABLED_ERROR,
    });
  };
};

const dispatchEnableHelenaStart = () => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: ENABLE_HELENA_START,
    });
  };
};

const dispatchEnableHelenaSuccess = () => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: ENABLE_HELENA_SUCCESS,
    });
  };
};

const dispatchEnableHelenaError = () => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: ENABLE_HELENA_ERROR,
    });
  };
};

const dispatchHelenaSessionStart = () => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: SESSION_HELENA_START,
    });
  };
};

const dispatchHelenaSessionSuccess = (sessionKey: HelenaSessionKey) => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: SESSION_HELENA_SUCCESS,
      payload: sessionKey,
    });
  };
};

const dispatchHelenaSessionError = () => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: SESSION_HELENA_ERROR,
    });
  };
};

const dispatchHelenaPatientStatusStart = () => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: PATIENT_STATUS_HELENA_START,
    });
  };
};

const dispatchHelenaPatientStatusSuccess = (
  patientStatus: HelenaPatientStatusEnum
) => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: PATIENT_STATUS_HELENA_SUCCESS,
      payload: patientStatus,
    });
  };
};

const dispatchHelenaPatientIdStart = () => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: PATIENT_ID_HELENA_START,
    });
  };
};

const dispatchHelenaPatientIdSuccess = (response: string) => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: PATIENT_ID_HELENA_SUCCESS,
      payload: response,
    });
  };
};

const dispatchHelenaPatientIdError = (error: any) => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: PATIENT_ID_HELENA_ERROR,
      payload: error,
    });
  };
};

const dispatchHelenaPatientStatusError = (error: any) => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: PATIENT_ID_HELENA_ERROR,
      payload: error,
    });
  };
};

const dispatchHelenaRegistrationLinkStart = () => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: REGISTRATION_LINK_HELENA_START,
    });
  };
};

const dispatchHelenaRegistrationLinkSuccess = (response: string) => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: REGISTRATION_LINK_HELENA_SUCCESS,
      payload: response,
    });
  };
};

const dispatchHelenaRegistrationLinkError = () => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: REGISTRATION_LINK_HELENA_ERROR,
    });
  };
};

const dispatchHelenaRegistrationFinishedStart = () => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: REGISTRATION_FINISHED_HELENA_START,
    });
  };
};

const dispatchHelenaRegistrationFinishedSuccess = (
  response: HelenaRegistration
) => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: REGISTRATION_FINISHED_HELENA_SUCCESS,
      payload: response,
    });
  };
};

const dispatchHelenaRegistrationFinishedError = () => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: REGISTRATION_FINISHED_HELENA_ERROR,
    });
  };
};

const dispatchHelenaPrescriptionsSuccess = () => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: PRESCRIPTIONS_HELENA,
    });
  };
};

const dispatchHelenaPrescriptionsStart = (
  prescriptions: Array<HelenaPrescriptionType>
) => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({
      type: PRESCRIPTIONS_HELENA_START,
      payload: prescriptions,
    });
  };
};

const dispatchHelenaAttestationStart = (document: HelenaDocumentType) => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({ type: ATTESTATION_HELENA_START, payload: document });
  };
};

const dispatchHelenaAttestation = (document: HelenaDocumentType) => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({ type: ATTESTATION_HELENA, payload: document });
  };
};

const dispatchHelenaAttestationError = (error: any) => {
  return (dispatch: Dispatch<HelenaAction>) => {
    dispatch({ type: ATTESTATION_HELENA_ERROR, payload: error });
  };
};

const isSessionHelenaExpired = (sessionKey: HelenaSessionKey): boolean => {
  return !!(
    sessionKey.expiryDate && new Date(sessionKey.expiryDate) < new Date()
  );
};
